import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Modal,
  FileInput,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import { getFileValue } from 'helpers';
import { downloadTrainingsImportTemplate } from '../../../../api/services/training';

const ImportTraining = ({
  state,
  dispatch,
  updateFieldCreator,
  setErrorsCreator,
  importData,
  importErrors,
}) => {
  const { t } = useTranslation();
  const { data, errors } = state;

  const [openModal, setOpenModal] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloadingTemplate(true);
      await downloadTrainingsImportTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleChangeFileNative = (name, errorName) => (e) => {
    dispatch(
      updateFieldCreator({
        name,
        value: e.target.files && e.target.files.length ? e.target.files[0] : null,
      }),
    );
    if (errors[errorName]) dispatch(setErrorsCreator(clearErrors(errors, errorName)));
  };

  const handleClearFileNative = (name) => () => {
    dispatch(updateFieldCreator({ name, value: null }));
  };

  const submit = () => {
    const params = new FormData();
    params.append('file', getFileValue(data.file));

    importData(params);
  };

  const renderImportErrors = () => {
    if (!importErrors || !importErrors.length) return null;

    const columns = [
      { key: 'rowNum', label: t('page.ma.cpdManagement.trainings.import.rowNum') },
      { key: 'userId', label: t('page.ma.cpdManagement.trainings.import.userId') },
      { key: 'courseId', label: t('page.ma.cpdManagement.trainings.import.courseId') },
      { key: 'name', label: t('page.ma.cpdManagement.trainings.import.name') },
      { key: 'error', label: t('page.ma.cpdManagement.trainings.import.reason') },
    ];

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    borderBottom: '2px solid #eee',
                    padding: '12px 16px',
                    background: '#fafbfc',
                    textAlign: 'left',
                    fontWeight: 600,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {importErrors.map((entry, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                {columns.map((col) => {
                  if (col.key === 'error') {
                    return (
                      <td
                        key={col.key}
                        style={{ color: '#c00', padding: '12px 16px', verticalAlign: 'top' }}
                      >
                        {Array.isArray(entry.error) ? (
                          <ul style={{ margin: 0 }}>
                            {entry.error.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        ) : (
                          entry.error
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={col.key} style={{ padding: '12px 16px' }}>
                      {entry.row && entry.row[col.key] ? entry.row[col.key] : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ textAlign: 'right', width: '6%', margin: 10, float: 'right' }}>
          <Button type="button" onClick={() => setOpenModal(false)}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.ma.cpdManagement.trainings.import.title')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="file"
                  label={t('page.ma.cpdManagement.trainings.import.uploadFile')}
                  error={errors.file}
                  description={t('global.uploadExcelInfo')}
                >
                  <FileInput
                    id="file"
                    placeholder={t('global.uploadPlaceholder')}
                    fileName={data.file ? data.file.name || data.file.fileName : ''}
                    clear={handleClearFileNative('file')}
                    onChange={handleChangeFileNative('file', 'file')}
                    accept=".xls,.xlsx"
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.import')}
                </Button>
              </Margin>
            </Col>
            {importErrors && importErrors.length > 0 && (
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom={{ xs: 12, md: 0 }}>
                  <Button type="button" onClick={() => setOpenModal(true)}>
                    {t('global.viewErrors')}
                  </Button>
                </Margin>
              </Col>
            )}
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary disabled={isDownloadingTemplate} onClick={handleDownload}>
                {isDownloadingTemplate ? t('global.downloading') : t('global.downloadTemplate')}
              </Button>
            </Col>
          </Row>
        </form>
      </Container>
      <Modal
        customContent={renderImportErrors}
        isOpen={openModal}
        confirm={() => setOpenModal(false)}
        cancel={() => setOpenModal(false)}
        onRequestClose={() => setOpenModal(false)}
        autoWidth
      />
    </PageContent>
  );
};

ImportTraining.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  setErrorsCreator: PropTypes.func.isRequired,
  importData: PropTypes.func.isRequired,
  importErrors: PropTypes.array,
};

ImportTraining.defaultProps = {
  importErrors: [],
};

export default ImportTraining;
